import React, { useEffect, useState } from 'react'
import { Upload, Trash2 } from 'react-feather'
import FileUploaderWithActions from '../common/form-elements/FileUploaderWithActions'
import { ErrorType, useCSVValidator } from 'src/lib/hooks/useCSVValidator'
import { FileTypeOptions, UploadedFileType } from 'src/lib/types/common/files'
import NewGenericDialog from '../common/feedback-and-dialog/NewGenericDialog'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { parse } from 'papaparse'
import { BoxArrowUp, Trash, Warning } from '@phosphor-icons/react'
import TextFieldError from '../common/form-elements/TextFieldError'
import { useLocale } from 'src/lib/hooks/useLocal'

interface CSVImportModalProps {
  isOpen: boolean
  handleClose: () => void
  onFileUploaded: (fileData: UploadedFileType, rawFile: FormData) => void
  acceptedSize: number
  type: FileTypeOptions
  errorsToCheck?: ErrorType[]
  template?: Record<string, string>
  successComponent?: React.ReactNode
  sampleLink: string
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({
  isOpen,
  handleClose,
  onFileUploaded,
  acceptedSize,
  type,
  errorsToCheck = [],
  template,
  successComponent,
  sampleLink
}) => {
  const { t } = useLocale()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [uploadedFile, setUploadedFile] = useState<UploadedFileType | null>(null)
  const [uploadedFileFormData, setUploadedFileFormData] = useState<FormData | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleDone = () => {
    if (uploadedFile && uploadedFileFormData) {
      onFileUploaded(uploadedFile, uploadedFileFormData)
    }
    handleClose()
  }

  const deleteUploadedFile = () => {
    setUploadedFile(null)
    setUploadedFileFormData(null)
  }

  useEffect(() => {
    deleteUploadedFile()
  }, [isOpen])

  const {
    loadFile,
    errors,
    resetErrors: resetCSVValidatorErrors,
    isValidating
  } = useCSVValidator({
    errorsToCheck,
    template
  })

  const sizeCheck = (file: any) => {
    if (file?.size > acceptedSize) {
      setErrorMessage(t('segment_creation.file_size_exceeded', { size: acceptedSize / (1024 * 1024) }))
      setIsUploading(false)
      return false
    }
    return true
  }

  const typeCheck = (file: any) => {
    const acceptedTypes = Array.isArray(type) ? type : [type]
    const fileType = file.type.toLowerCase()
    const fileExtension = file.name.split('.').pop()?.toLowerCase()

    if (
      !acceptedTypes.some(
        acceptedType =>
          acceptedType.toLowerCase().includes(fileType) || acceptedType.toLowerCase().includes(fileExtension)
      )
    ) {
      setErrorMessage(t('segment_creation.invalid_file_type', { type: Array.isArray(type) ? type.join(', ') : type }))
      setIsUploading(false)
      return false
    }
    return true
  }

  async function fileUpload(e: any) {
    setErrorMessage(null)
    resetCSVValidatorErrors()
    if (!e.target?.files?.[0]) return
    setIsUploading(true)

    const file = e.target.files?.[0]
    if (!sizeCheck(file) || !typeCheck(file)) {
      setIsUploading(false)
      return
    }
    const formData = new FormData()
    formData.append('file', file)

    try {
      const validationErrors = await loadFile(file)
      if (validationErrors.length > 0) {
        setIsUploading(false)
        return
      }

      postRequest(`/attributes/v1/files/upload`, apiURI.FLO_CHECKOUT, formData)
        .then((data: any) => {
          setIsUploading(false)
          parse(file, {
            header: true,
            complete: results => {
              setUploadedFile({
                name: file?.name,
                url: data?.data?.file_url,
                size: file?.size,
                count: results?.data?.map((row: any) => Object.values(row)[0] as string).filter(Boolean)?.length,
                type: type
              })
              setUploadedFileFormData(formData)
            }
          })
          successToast(t('segment_creation.upload_successful'))
        })
        .catch(error => {
          console.error(error)
          setErrorMessage(t('segment_creation.upload_error'))
          setIsUploading(false)
        })
    } catch (error) {
      console.error(error)
      setIsUploading(false)
      setErrorMessage(t('segment_creation.upload_error'))
    }
  }

  const handleCancel = () => {
    handleClose()
  }

  return (
    <NewGenericDialog
      isOpen={isOpen}
      onClose={handleCancel}
      icon={<Upload size={20} />}
      title={t('segment_creation.import_csv')}
      primaryButtonText={t('segment_creation.done')}
      secondaryButtonText={t('segment_creation.cancel')}
      isPrimaryButtonDisabled={!uploadedFile}
      onPrimaryClick={handleDone}
      onSecondaryClick={handleCancel}
      width={600}
    >
      <div className='flex flex-col gap-4'>
        {/* Download sample link */}
        <p className='text-text-xs-regular text-gray-500'>
          {t('segment_creation.need_sample_csv')}{' '}
          <a href={sampleLink} download className='font-medium underline text-gray-600 hover:text-coal-dark'>
            {t('segment_creation.download')}
          </a>
        </p>

        {/* Upload Area */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-md p-6 transition-colors
          ${uploadedFile ? 'border-success-light bg-success-extralight' : 'border-slate-200 bg-white'}
          ${isUploading ? 'opacity-60 pointer-events-none' : ''}
        `}
        >
          {uploadedFile ? (
            <div className='flex flex-col items-center gap-2'>
              <div className='flex items-center gap-2'>
                <span className='text-text-md-medium text-success-dark'>{t('segment_creation.upload_complete')}</span>
              </div>
              <div className='flex flex-row justify-center items-center divide-x divide-gray-200 space-x-2'>
                <span className='text-text-xs-regular text-success-dark'>{uploadedFile?.name}</span>
                <button className='pl-2' onClick={deleteUploadedFile}>
                  <Trash size={20} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <input
                type='file'
                accept='.csv'
                id='csvFileInput'
                className='hidden'
                onChange={fileUpload}
                disabled={isUploading}
              />
              <label
                htmlFor='csvFileInput'
                className={`flex flex-col items-center justify-center cursor-pointer w-full
            ${isUploading ? 'cursor-not-allowed' : ''}
          `}
              >
                <div className='flex flex-col items-center justify-center gap-2'>
                  <BoxArrowUp size={32} />
                  <span className='text-text-md-medium text-gray-800'>{t('segment_creation.click_to_upload')}</span>
                  <span className='text-text-xs-regular text-gray-500'>
                    {t('segment_creation.csv_file_size_limit', { size: acceptedSize / (1024 * 1024) })}
                  </span>
                </div>
              </label>
              {isUploading && (
                <span className='mt-3 text-text-xs-regular text-gray-600'>{t('segment_creation.uploading')}</span>
              )}
            </>
          )}
        </div>
        {errors.length > 0 && (
          <div className='flex justify-start items-center text-red-dark space-x-2'>
            <Warning size={20} />
            <TextFieldError message={errors[0]} />
          </div>
        )}
        {errorMessage && (
          <div className='flex justify-start items-center text-red-dark space-x-2'>
            <Warning size={20} />
            <TextFieldError message={errorMessage} />
          </div>
        )}
        {uploadedFile && successComponent}
      </div>
    </NewGenericDialog>
  )
}

export default CSVImportModal
